import { useState, useEffect } from 'react';
import { Clock, RefreshCw, LogOut } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface SessionExpiredDialogProps {
  open: boolean;
  onRefresh: () => void;
  onLogout: () => void;
}

export function SessionExpiredDialog({ open, onRefresh, onLogout }: SessionExpiredDialogProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="sm:max-w-md border-0 shadow-2xl bg-gradient-to-br from-background via-background to-muted/30">
        {/* Decorative gradient circle */}
        <div className="absolute -top-12 left-1/2 -translate-x-1/2">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-pulse">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/25">
                <Clock className="w-8 h-8 text-primary-foreground" />
              </div>
            </div>
          </div>
        </div>

        <AlertDialogHeader className="pt-14 text-center">
          <AlertDialogTitle className="text-xl font-semibold">
            Session expirée
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground mt-2">
            Votre session a expiré pour des raisons de sécurité. 
            Actualisez la page pour continuer à travailler.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-6">
          <Button
            variant="outline"
            onClick={onLogout}
            className="flex-1 gap-2"
          >
            <LogOut className="w-4 h-4" />
            Se déconnecter
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex-1 gap-2 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg shadow-primary/25"
          >
            {isRefreshing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Actualisation...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Actualiser la page
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
